"use client";

import { cn } from "@/lib/utils";
import type { AirGPUMachineStatus } from "@/lib/integrations/airgpu/types";

/**
 * Every status a machine row can hold: the upstream API's states plus our own
 * `pending` (creation in flight) and `failed`. A soft-deleted machine is a row
 * with `deletedAt` set, not a status.
 */
export type MachineStatus = AirGPUMachineStatus | "pending" | "failed";

interface MachineStatusBadgeProps {
  /**
   * A MachineStatus, but typed as any string: a status the API adds later
   * must neither crash the page nor be shown as something it is not.
   */
  status: string;
  className?: string;
  showDot?: boolean;
  size?: "sm" | "default";
}

interface StatusConfig {
  label: string;
  /** Chip / tile fill: a translucent tint, or `bg-muted` for the neutral states. */
  bgColor: string;
  /** Text and icon colour, lifted for dark mode so it reads on either ground. */
  textColor: string;
  /** Solid dot. No text sits on it, so the -500 hue is fine in both modes. */
  dotColor: string;
  borderColor: string;
  pulse?: boolean;
}

/*
 * Colour families. Running is emerald: the machine is healthy and billing.
 * Every transitional state is amber, failed is red, and the states with
 * nothing happening (off, creating) are neutral. Status hues stay named
 * Tailwind colours on purpose: a palette must not turn "failed" blue.
 */
const RUNNING = {
  bgColor: "bg-emerald-500/10",
  textColor: "text-emerald-700 dark:text-emerald-400",
  dotColor: "bg-emerald-500",
  borderColor: "border-emerald-500/20",
} as const;

const TRANSITIONAL = {
  bgColor: "bg-amber-500/10",
  textColor: "text-amber-700 dark:text-amber-400",
  dotColor: "bg-amber-500",
  borderColor: "border-amber-500/20",
  pulse: true,
} as const;

const FAILED = {
  bgColor: "bg-red-500/10",
  textColor: "text-red-600 dark:text-red-400",
  dotColor: "bg-red-500",
  borderColor: "border-red-500/20",
} as const;

// Off and pending are low emphasis by meaning, so (unlike every other status)
// they sit on bg-muted. The dot is muted-foreground: a bg-muted dot on a
// bg-muted chip would vanish.
const NEUTRAL = {
  bgColor: "bg-muted",
  textColor: "text-muted-foreground",
  dotColor: "bg-muted-foreground",
  borderColor: "border-border",
} as const;

/**
 * The one place machine statuses get their customer-facing names and colours.
 * Pages and redesigns should render <MachineStatusBadge>, or read
 * getMachineStatusConfig / getStatusLabel / getTransitionLabel, rather than
 * growing their own `switch (status)` that misses a state -- or their own
 * status colours.
 */
const statusConfig: Record<MachineStatus, StatusConfig> = {
  off: { label: "Off", ...NEUTRAL },
  starting: { label: "Starting", ...TRANSITIONAL },
  starting_pending: { label: "Queued", ...TRANSITIONAL },
  initializing: { label: "Initializing", ...TRANSITIONAL },
  running: { label: "Running", ...RUNNING, pulse: true },
  stopping: { label: "Stopping", ...TRANSITIONAL },
  backing_up: { label: "Backing Up", ...TRANSITIONAL },
  // Our own row exists, the create call is still in flight.
  pending: { label: "Creating", ...NEUTRAL, pulse: true },
  failed: { label: "Failed", ...FAILED },
};

function isKnownStatus(status: string): status is MachineStatus {
  return Object.hasOwn(statusConfig, status);
}

/** "some_new_state" -> "Some new state". */
function humanizeStatus(status: string): string {
  const words = status.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Unknown";
}

/**
 * Display config for a status. An unknown status (one the API added after this
 * was written) gets a neutral badge with its own name -- never "Off", which
 * would tell the customer a machine that may be running and billing is off.
 */
export function getMachineStatusConfig(status: string): StatusConfig {
  if (isKnownStatus(status)) return statusConfig[status];
  return { label: humanizeStatus(status), ...NEUTRAL };
}

/** Customer-facing name for a status ("Queued", "Creating", "Backing Up"...). */
export function getStatusLabel(status: string): string {
  return getMachineStatusConfig(status).label;
}

export function MachineStatusBadge({
  status,
  className,
  showDot = true,
  size = "default",
}: MachineStatusBadgeProps) {
  const config = getMachineStatusConfig(status);

  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border font-medium",
        size === "sm" ? "gap-1 px-2 py-0.5 text-[10px]" : "gap-1.5 px-2.5 py-1 text-xs",
        config.bgColor,
        config.textColor,
        config.borderColor,
        className
      )}
    >
      {showDot && (
        <span
          aria-hidden
          className={cn(
            "rounded-full",
            size === "sm" ? "size-1" : "size-1.5",
            config.dotColor,
            config.pulse && "animate-pulse"
          )}
        />
      )}
      {config.label}
    </span>
  );
}

export function isTransitioningStatus(status: string): boolean {
  return ["starting", "starting_pending", "initializing", "stopping", "backing_up", "pending"].includes(status);
}

/**
 * Label for the disabled spinner button shown while a machine is in one of
 * the transitional states above.
 */
export function getTransitionLabel(status: string): string {
  switch (status) {
    case "starting":
    case "initializing":
      return "Starting...";
    case "starting_pending":
      return "Queued...";
    case "pending":
      return "Creating...";
    case "backing_up":
      return "Backing up...";
    case "stopping":
      return "Stopping...";
    default:
      return "Updating...";
  }
}
